import * as React from 'react';

import { ListView } from '@longline/aqua-ui/controls/ListView/ListView';
import { SecondaryButton } from '@longline/aqua-ui/controls/SecondaryButton';
import { Icon } from '@longline/aqua-ui/controls/Icon';
import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { SVG } from '@longline/aqua-ui/svg';
import { Key } from '@longline/aqua-ui/controls/Key';
import { Message } from '@longline/aqua-ui/containers/Message';

import { useGlobalState } from '../../contexts/state/GlobalStateContext';
import { IReference } from '../../types/IReference';
import { ReferenceInfoBox } from '../../infoboxes/ReferenceInfoBox';
import { useConfirm } from '../../contexts/confirm/ConfirmContext';
import { Guide } from '../../ui/Guide';
import { Pane } from '../../ui/Pane';

const ReferencePane = () => {
  const confirm = useConfirm();
  const globalState = useGlobalState();

  const handleDeleteAll = () => {
    globalState.clearReferences();
  }

  return (
    <>
      <Pane.Heading>
        <Pane.Title>
          Reference layers
        </Pane.Title>
      </Pane.Heading>
      <Pane.Subheader expandable action={<SecondaryButton title="Import" positive icon={SVG.Icons.Plus} onClick={globalState.startReferenceImport}/>}>
        Import layer
      </Pane.Subheader>

      <ListView 
        dark 
        data={globalState.references} 
        active={globalState.selectedReference}
        onClick={(reference) => globalState.setSelectedReference(reference)} 
        shadow
        aux={<ReferenceInfoBox/>}
        total
        singular="layer"
        plural="layers"
        noData={<Message margin>There are no reference layers yet.</Message>}
        rowHeight={56}
      >
        <ListView.Column active width={3} name="Name">{(item: IReference) => item.name}</ListView.Column>
        <ListView.Column active={globalState.expanded} width="80px" name="Features" align="right">{(item: IReference) => <NumberFormatter value={item.geojson?.features.length ?? 0} decimals={0}/>}</ListView.Column>
        <ListView.Column active width="64px" name="">{(item: IReference) => <Icon url={item.visible ? SVG.Icons.Eye : SVG.Icons.EyeClosed}/>}</ListView.Column>
      </ListView>
      
      {globalState.references.length > 0 &&
        <Pane.Fab negative title="Delete all layers" icon={SVG.Icons.Delete} onClick={() => confirm.start(handleDeleteAll, "Delete all reference layers", "Are you sure you wish to delete all reference layers?")}/>}

      {globalState.selectedReference == null && globalState.selectedStructures.length === 0 &&
        <Pane.Guide>
          <Guide x={0} y={0} title="Reference layer management">
            Click <Key value="+"/> to create a new layer from a GeoJSON file.
          </Guide>
        </Pane.Guide>}

      {globalState.selectedReference != null && globalState.selectedStructures.length === 0 &&
        <Pane.Guide>
          <Guide x={0} y={0} title="Editing a reference layer">
            <ul>
              <li>Click <Icon url={SVG.Icons.Eye}/> to show or hide a layer.</li>
              <li>Click <Icon url={SVG.Icons.Delete}/> to remove a layer from the map.</li>
              <li>Select a GeoJSON property to render with color classification.</li>
              <li>Edit opacity and gradient to change layer visualization.</li>
            </ul>
          </Guide>
        </Pane.Guide>}    
    </>
  )
}

export { ReferencePane }
